import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import PayButton from './paypal/PayButton';
import '../css/PaymentInstructions.css';

const PaymentInstructions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [programDetails, setProgramDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('e-transfer');

  // Base URL for API calls
  const baseUrl = process.env.REACT_APP_NODE_ENV === 'production'
    ? process.env.REACT_APP_PRODUCTION_BASE_URL
    : process.env.REACT_APP_DEVELOPMENT_BASE_URL;

  useEffect(() => {
    const loadData = async () => {
      try {
        // Parse query parameters first
        const queryParams = new URLSearchParams(location.search);
        const method = queryParams.get('method');
        const amount = queryParams.get('amount');
        const userId = queryParams.get('userId');
        const programId = queryParams.get('programId');
        const programName = queryParams.get('programName');

        // Try to get payment details from session storage
        const storedDetails = window.sessionStorage.getItem('paymentDetails');
        let details = null;

        if (storedDetails) {
          details = JSON.parse(storedDetails);
        }

        // Merge stored details with query parameters (give priority to query params)
        const mergedDetails = {
          ...(details || {}),
          userId: userId || details?.userId,
          amount: amount || details?.amount,
          programId: programId || details?.programId,
          programName: programName || details?.programName,
          paymentMethod: method || details?.paymentMethod || 'e-transfer',
          paymentStatus: 'pending',
          timestamp: new Date().toISOString()
        };

        setPaymentDetails(mergedDetails);
        if (mergedDetails.paymentMethod) {
          setSelectedPaymentMethod(mergedDetails.paymentMethod);
        }

        // If we have programId but no program details, fetch them
        if (mergedDetails.programId && !mergedDetails.programName) {
          await fetchProgramDetails(mergedDetails.programId);
        } else {
          setLoading(false);
        }

        // Save updated payment details to session storage
        window.sessionStorage.setItem('paymentDetails', JSON.stringify(mergedDetails));
      } catch (err) {
        console.error('Failed to process payment details:', err);
        setLoading(false);
      }
    };

    loadData();
  }, [location.search]);

  const fetchProgramDetails = async (programId) => {
    try {
      const response = await axios.get(`${baseUrl}/api/course/${programId}`);
      if (response.data.success) {
        const program = response.data.data;
        setProgramDetails(program);

        // Update payment details with program information
        const updatedDetails = {
          ...paymentDetails,
          programName: program.programName,
          amount: program.programPrice
        };

        setPaymentDetails(updatedDetails);
        window.sessionStorage.setItem('paymentDetails', JSON.stringify(updatedDetails));
      }
    } catch (err) {
      console.error('Error fetching program details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodChange = (method) => {
    setSelectedPaymentMethod(method);

    // Update payment details with new method
    if (paymentDetails) {
      const updatedDetails = {
        ...paymentDetails,
        paymentMethod: method
      };

      setPaymentDetails(updatedDetails);
      window.sessionStorage.setItem('paymentDetails', JSON.stringify(updatedDetails));
    }
  };

  const handlePaypalValidation = async () => {
    // Simply return the payment details to continue with PayPal
    // This will be used by the PayButton component
    return { success: true, userId: paymentDetails?.userId };
  };

  if (loading) {
    return (
      <div className="payment-instructions-page loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading payment instructions...</p>
      </div>
    );
  }

  // Get amount from payment details or program details
  const amount = paymentDetails?.amount || (programDetails ? programDetails.programPrice : '0.00');

  return (
    <section className="payment-instructions-section">
      <div className="container">
        <div className="row">
          <div className="col-lg-6 mb-3 mb-md-0">
            <div className="position-stick-top">
              <h4>Complete Your Payment</h4>
              {paymentDetails && (
                <div className="payment-summary mb-4">
                  <div className="detail-row">
                    <span className="detail-label">Program:</span>
                    <span className="detail-value">{paymentDetails.programName}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Amount:</span>
                    <span className="detail-value">${Number(amount).toFixed(2)} CAD</span>
                  </div>
                </div>
              )}

              <div className="payment-method-selector mb-4">
                <div className="payment-methods">
                  <div
                    className={`payment-method-option ${selectedPaymentMethod === 'e-transfer' ? 'selected' : ''}`}
                    onClick={() => handlePaymentMethodChange('e-transfer')}
                  >
                    <div className="option-icon">
                      <i className="fa fa-university"></i>
                    </div>
                    <div className="option-label">E-Transfer</div>
                  </div>
                  <div
                    className={`payment-method-option ${selectedPaymentMethod === 'paypal' ? 'selected' : ''}`}
                    onClick={() => handlePaymentMethodChange('paypal')}
                  >
                    <div className="option-icon">
                      <i className="fa fa-paypal"></i>
                    </div>
                    <div className="option-label">PayPal</div>
                  </div>
                  <div
                    className={`payment-method-option ${selectedPaymentMethod === 'inOffice' ? 'selected' : ''}`}
                    onClick={() => handlePaymentMethodChange('inOffice')}
                  >
                    <div className="option-icon">
                      <i className="fa fa-building"></i>
                    </div>
                    <div className="option-label">At Office</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="content">
              {(selectedPaymentMethod === 'e-transfer' || selectedPaymentMethod === "inOffice") ? (
                <div className="e-transfer-instructions">
                  <div className="instruction-card">
                    <div className="card-header">
                      <i className="fa fa-university"></i>
                      <h5>E-Transfer Details</h5>
                    </div>
                    <div className="card-body">
                      <div className="transfer-details">
                        <div className="detail-item">
                          <span className="label">Send to:</span>
                          <span className="value">payments@confidentdriver.com</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Amount:</span>
                          <span className="value">${Number(amount).toFixed(2)} CAD</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Reference:</span>
                          <span className="value">REG-{paymentDetails?.userId || 'REGISTRATION'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Security Question:</span>
                          <span className="value">What is my program name?</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Answer:</span>
                          <span className="value">{paymentDetails?.programName || programDetails?.programName || 'DriverEd'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="action-buttons">
                    <Link to="/" className="btn btn-outline">Return Home</Link>
                    <Link to="/payment-success" className="btn btn1">Payment Complete</Link>
                  </div>
                </div>
              ) : (
                <div className="paypal-section">
                  <div className="instruction-card">
                    <div className="card-header">
                      <i className="fa fa-paypal"></i>
                      <h5>PayPal Payment</h5>
                    </div>
                    <div className="card-body">
                      <div className="paypal-button-wrapper">
                        <PayPalScriptProvider
                          options={{
                            "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID || "sb",
                            currency: "CAD",
                            intent: "capture"
                          }}
                        >
                          <PayButton
                            amount={amount}
                            onClick={handlePaypalValidation}
                            userData={paymentDetails}
                            programInfo={{
                              programId: paymentDetails?.programId || '',
                              programName: paymentDetails?.programName || programDetails?.programName || ''
                            }}
                          />
                        </PayPalScriptProvider>
                        <p className="paypal-info">
                          Click the PayPal button above to complete your payment securely.
                          You can pay using your PayPal balance, credit card, or bank account.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="action-buttons">
                    <Link to="/" className="btn btn-outline">Return Home</Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PaymentInstructions; 